import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Linkedin, FileText, Crown } from 'lucide-react';
import { gsap, useRevealOnScroll } from '../../hooks/useGsap';
import { managementData } from '../../data/managementData';

const TeamMember = ({ name, role, photo, linkedin, size = 'md' }) => {
  const sizes = {
    sm: 'w-20 h-20',
    md: 'w-24 h-24',
    lg: 'w-28 h-28',
  };
  return (
    <div className="flex flex-col items-center group">
      <div className="relative mb-3">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-400 to-primary-700 blur-md opacity-0 group-hover:opacity-60 transition-opacity duration-300" />
        <img
          src={photo}
          alt={name}
          className={`relative ${sizes[size]} rounded-full object-cover ring-4 ring-white/10 shadow-md group-hover:scale-105 transition-transform duration-300`}
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=16a34a&color=fff&size=120`;
          }}
        />
        {linkedin && (
          <a
            href={linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute -bottom-1 -right-1 bg-primary-500 p-2 rounded-full text-night-950 hover:bg-primary-300 transition-colors shadow-md"
          >
            <Linkedin size={14} />
          </a>
        )}
      </div>
      <p className="font-semibold text-center text-mist-100 text-sm">{name}</p>
      {role && <p className="text-xs text-mist-500 text-center mt-0.5 max-w-[140px]">{role}</p>}
    </div>
  );
};

const CollapsibleSection = ({ title, badge, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!bodyRef.current) return;
    if (isOpen) {
      gsap.fromTo(
        bodyRef.current,
        { height: 0, opacity: 0 },
        { height: 'auto', opacity: 1, duration: 0.55, ease: 'power3.out' }
      );
    }
  }, [isOpen]);

  return (
    <div className="bg-night-800 border border-white/[0.07] rounded-3xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-7 py-6 flex items-center justify-between hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center gap-4">
          <span className="font-display text-2xl md:text-3xl font-bold text-mist-50 tracking-tight">{title}</span>
          {badge && <span className="pill">{badge}</span>}
        </div>
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.07] text-mist-50">
          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {isOpen && (
        <div ref={bodyRef} className="overflow-hidden">
          <div className="px-7 pb-8 border-t border-white/[0.07]">{children}</div>
        </div>
      )}
    </div>
  );
};

const ManagementBlock = ({ leadership, productOwners, directors }) => (
  <div className="space-y-12 pt-8">
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Crown size={16} className="text-primary-400" />
        <span className="eyebrow">Presidência</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
        {leadership.map((leader, idx) => (
          <TeamMember key={idx} {...leader} size="lg" />
        ))}
      </div>
    </div>

    <div>
      <span className="eyebrow">Product Owners</span>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8">
        {productOwners.map((po, idx) => (
          <TeamMember key={idx} {...po} />
        ))}
      </div>
    </div>

    <div className="space-y-10">
      <span className="eyebrow">Diretoria</span>
      {directors.map((director, idx) => (
        <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start pt-6 border-t border-white/[0.07] first:border-t-0">
          <div className="md:col-span-3 flex md:block diretor">
            <TeamMember {...director} />
          </div>
          {director.coordinators && director.coordinators.length > 0 ? (
            <div className="md:col-span-9 mt-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                {director.coordinators.map((coord, coordIdx) => (
                  <TeamMember key={coordIdx} {...coord} size="sm" />
                ))}
              </div>
            </div>
          ) : (
            <div className="md:col-span-9" />
          )}
        </div>
      ))}
    </div>
  </div>
);

const Management = () => {
  const ref = useRevealOnScroll();

  return (
    <section id="management" ref={ref} className="relative py-28 bg-night-950 overflow-hidden">
      <div className="absolute top-10 -right-10 pointer-events-none select-none">
        <span className="font-display text-[16rem] font-black text-stroke opacity-[0.04] leading-none">TIME</span>
      </div>

      <div className="container-custom relative">
        <div className="max-w-3xl mb-14">
          <span className="eyebrow reveal">Nossa gestão</span>
          <h2 className="section-title reveal mt-4">
            Conheça o time que <span className="text-gradient">lidera</span> a AgroTech Inteli
          </h2>
        </div>

        <div className="reveal space-y-5">
          <CollapsibleSection title="Gestão 2026" badge="Atual" defaultOpen>
            <ManagementBlock
              leadership={managementData.leadership2026}
              productOwners={managementData.productOwners26}
              directors={managementData.directors26}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Gestão 2025">
            <ManagementBlock
              leadership={managementData.leadership2025}
              productOwners={managementData.productOwners}
              directors={managementData.directors}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Fundadores">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 pt-8">
              {managementData.founders.map((founder, idx) => (
                <TeamMember key={idx} {...founder} size="lg" />
              ))}
            </div>
          </CollapsibleSection>

          <div className="reveal relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-800 to-primary-950 text-white p-8 md:p-10 border border-primary-400/20">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-primary-400/25 blur-3xl" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-300">Documento Oficial</span>
                <h3 className="font-display text-2xl md:text-3xl font-bold mt-2">Regimento Interno</h3>
                <p className="text-primary-100/80 mt-2 max-w-md">Acesse nosso regimento interno completo</p>
              </div>
              <a
                href="/media/regimento-interno.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary-400 text-night-950 hover:bg-primary-300 font-semibold py-3.5 px-7 rounded-full transition-all duration-300 shadow-lg"
              >
                <FileText size={18} />
                Acessar Regimento
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Management;
